/**
 * The connection-lost line (25 Oct 2026, D-1025-NAV): when a read cannot reach the API at all (a network failure, or the
 * browser says it is offline), a polite line under the header says so and when the screen last loaded; it leaves by itself
 * as soon as any read gets an answer. A server's answer (even an error) is not "cannot reach" — only a failed fetch is.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

/** A fetch that never got an answer: the browser throws a TypeError ("Failed to fetch", "Load failed", "NetworkError…"). */
export function isUnreachable(error: unknown): boolean {
  return error instanceof TypeError;
}

export function formatNyTime(ms: number, lng: string): string {
  try {
    return new Intl.DateTimeFormat(lng === 'es' ? 'es-US' : 'en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }).format(new Date(ms));
  } catch {
    return '';
  }
}

export function ConnectionLost() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [lost, setLost] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
  const [lastOk, setLastOk] = useState<number>(() => Date.now());
  useEffect(() => {
    const unsub = qc.getQueryCache().subscribe((e) => {
      if (e.type !== 'updated') return;
      if (e.action.type === 'success') {
        // only an answer that came over the network clears the line: a screen writing into its own cache (setQueryData,
        // `manual`) is not an answer, and while the browser says it is offline nothing has been reached (seen in e2e:
        // the line left by itself 1.5 s after going offline)
        if (e.action.manual || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
        setLost(false);
        setLastOk(Date.now());
      } else if (e.action.type === 'error' && isUnreachable(e.action.error)) {
        setLost(true);
      }
    });
    const down = () => setLost(true);
    const up = () => void qc.invalidateQueries({ refetchType: 'active' });
    window.addEventListener('offline', down);
    window.addEventListener('online', up);
    return () => {
      unsub();
      window.removeEventListener('offline', down);
      window.removeEventListener('online', up);
    };
  }, [qc]);
  const time = formatNyTime(lastOk, i18n.language);
  return (
    <div className="conn1025-wrap no-print" role="status" aria-live="polite">
      {lost ? (
        <p className="conn1025" data-testid="connection-lost">
          {time ? t('nav1025.lost', { time }) : t('nav1025.lostNoTime')}
        </p>
      ) : null}
    </div>
  );
}
